
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Edit3 } from "lucide-react";
import { Candidate, UpdateCandidateData } from "../types";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateCandidateContactInfoSchema, UpdateCandidateContactInfoSchema } from "../schemas/updateCandidateContactInfoSchema";
import { useUpdateCandidateProfile } from "@/hooks/mutations/useUpdateCandidateProfile";
import { Button } from "@/components/ui/button";
import { ContactInfoDisplay } from "./ContactInfoDisplay";
import { ContactInfoForm } from "./ContactInfoForm";

interface ContactInfoProps {
  candidate: Candidate;
}

export const ContactInfo = ({ candidate }: ContactInfoProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { updateCandidateProfile, isUpdatingProfile } = useUpdateCandidateProfile();

  const form = useForm<UpdateCandidateContactInfoSchema>({
    resolver: zodResolver(updateCandidateContactInfoSchema),
    defaultValues: {
      email: candidate.email || "",
      phone: candidate.phone || "",
      linkedin_url: candidate.linkedin_url || "",
    },
  });

  useEffect(() => {
    form.reset({
      email: candidate.email || "",
      phone: candidate.phone || "",
      linkedin_url: candidate.linkedin_url || "",
    });
  }, [candidate, form, isEditing]);

  const onSubmit = (values: UpdateCandidateContactInfoSchema) => {
    const dataToUpdate: UpdateCandidateData = {};

    if (values.email !== (candidate.email || "")) {
      dataToUpdate.email = values.email || undefined;
    }
    if (values.phone !== (candidate.phone || "")) {
      dataToUpdate.phone = values.phone || undefined;
    }
    if (values.linkedin_url !== (candidate.linkedin_url || "")) {
      dataToUpdate.linkedin_url = values.linkedin_url || undefined;
    }
    
    if (dataToUpdate.email === "") dataToUpdate.email = undefined;
    if (dataToUpdate.phone === "") dataToUpdate.phone = undefined;
    if (dataToUpdate.linkedin_url === "") dataToUpdate.linkedin_url = undefined;

    if (Object.keys(dataToUpdate).length > 0) {
      updateCandidateProfile(
        { candidateId: candidate.id, data: dataToUpdate },
        {
          onSuccess: (updatedCandidate) => {
            setIsEditing(false);
            form.reset({
              email: updatedCandidate?.email ?? candidate.email ?? "",
              phone: updatedCandidate?.phone ?? candidate.phone ?? "",
              linkedin_url: updatedCandidate?.linkedin_url ?? candidate.linkedin_url ?? "",
            });
          },
        }
      );
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-xl">
          <Mail className="h-5 w-5 text-primary" />
          Contact Information
        </CardTitle>
        {!isEditing && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIsEditing(true)}
            className="h-8 w-8"
          >
            <Edit3 className="h-4 w-4" />
            <span className="sr-only">Edit Contact Info</span>
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {isEditing ? (
          <ContactInfoForm
            form={form}
            onSubmit={onSubmit}
            onCancel={handleCancel}
            isUpdatingProfile={isUpdatingProfile}
          />
        ) : (
          <ContactInfoDisplay candidate={candidate} />
        )}
      </CardContent>
    </Card>
  );
};
